import { Data, Model, Utils } from '@singularsystems/neo-core';
import { AxiosPromise } from 'axios';
import { injectable } from 'inversify';
import { AppService, Types } from '../../DomainTypes';
import InvestorReferralType from '../../Models/Catalogue/Investments/InvestorReferralType';
import InternalRating from '../../Models/Catalogue/Investments/InternalRating';
import Entity from '../../Models/Catalogue/Investments/Entity';
import BankAccount from '../../Models/Catalogue/Investments/BankAccount';
import BankAccountBalanceLookupCriteria from '../../Models/Catalogue/Investments/Lookups/BankAccountBalanceLookupCriteria';
import BankAccountBalance from '../../Models/Catalogue/Investments/BankAccountBalance';
import NoticePeriod from '../../Models/Catalogue/Investments/NoticePeriod';

export interface IInvestmentApiClient {

    getInvestorReferralTypes(): AxiosPromise<Array<Model.PlainTrackedObject<InvestorReferralType>>>;

    saveInvestorReferralTypes(investorReferralTypes: Array<Model.PartialPlainObject<InvestorReferralType>>): AxiosPromise<Array<Model.PlainTrackedObject<InvestorReferralType>>>;

    getInternalRatings(): AxiosPromise<Array<Model.PlainTrackedObject<InternalRating>>>;

    saveInternalRatings(internalRatings: Array<Model.PartialPlainObject<InternalRating>>): AxiosPromise<Array<Model.PlainTrackedObject<InternalRating>>>;

    getEntities(): AxiosPromise<Array<Model.PlainTrackedObject<Entity>>>;

    saveEntities(entities: Array<Model.PartialPlainObject<Entity>>): AxiosPromise<Array<Model.PlainTrackedObject<Entity>>>;

    getBankAccounts(): AxiosPromise<Array<Model.PlainTrackedObject<BankAccount>>>;

    getBankAccountBalancesPaged(request: Model.PartialPlainNonTrackedObject<Data.PageRequest<BankAccountBalanceLookupCriteria>>): AxiosPromise<Data.PageResult<Model.PlainTrackedObject<BankAccountBalance>>>;

    deleteBankAccount(bankAccountId: number): AxiosPromise;

    saveBankAccount(bankAccount: Model.PartialPlainObject<BankAccount>): AxiosPromise<Model.PlainTrackedObject<BankAccount>>;

    saveBankAccountBalances(bankAccountBalances: Array<Model.PartialPlainObject<BankAccountBalance>>): AxiosPromise<Array<Model.PlainTrackedObject<BankAccountBalance>>>;

    getNoticePeriods(): AxiosPromise<Array<Model.PlainTrackedObject<NoticePeriod>>>;

    saveNoticePeriods(noticePeriods: Array<Model.PartialPlainObject<NoticePeriod>>): AxiosPromise<Array<Model.PlainTrackedObject<NoticePeriod>>>;

    // Client only properties / methods
}

@injectable()
export default class InvestmentApiClient extends Data.ApiClientBase implements IInvestmentApiClient {

    constructor (config = AppService.get(Types.App.Config)) {
        super(`${config.apiPath}/catalogue/investments`);
    }

    public getInvestorReferralTypes(): AxiosPromise<Array<Model.PlainTrackedObject<InvestorReferralType>>> {
        return this.axios.get(`${this.apiPath}/investor-referral-types`);
    }

    public saveInvestorReferralTypes(investorReferralTypes: Array<Model.PartialPlainObject<InvestorReferralType>>): AxiosPromise<Array<Model.PlainTrackedObject<InvestorReferralType>>> {
        return this.axios.post(`${this.apiPath}/investor-referral-types`, investorReferralTypes);
    }

    public getInternalRatings(): AxiosPromise<Array<Model.PlainTrackedObject<InternalRating>>> {
        return this.axios.get(`${this.apiPath}/internal-ratings`);
    }

    public saveInternalRatings(internalRatings: Array<Model.PartialPlainObject<InternalRating>>): AxiosPromise<Array<Model.PlainTrackedObject<InternalRating>>> {
        return this.axios.post(`${this.apiPath}/internal-ratings`, internalRatings);
    }

    public getEntities(): AxiosPromise<Array<Model.PlainTrackedObject<Entity>>> {
        return this.axios.get(`${this.apiPath}/entities`);
    }

    public saveEntities(entities: Array<Model.PartialPlainObject<Entity>>): AxiosPromise<Array<Model.PlainTrackedObject<Entity>>> {
        return this.axios.post(`${this.apiPath}/entities`, entities);
    }

    public getBankAccounts(): AxiosPromise<Array<Model.PlainTrackedObject<BankAccount>>> {
        return this.axios.get(`${this.apiPath}/bank-accounts`);
    }

    public getBankAccountBalancesPaged(request: Model.PartialPlainNonTrackedObject<Data.PageRequest<BankAccountBalanceLookupCriteria>>): AxiosPromise<Data.PageResult<Model.PlainTrackedObject<BankAccountBalance>>> {
        return this.axios.get(`${this.apiPath}/bank-account-balances-paged?${Utils.getQueryString(request)}`);
    }

    public deleteBankAccount(bankAccountId: number): AxiosPromise {
        return this.axios.post(`${this.apiPath}/delete-bank-account/${bankAccountId}`);
    }

    public saveBankAccount(bankAccount: Model.PartialPlainObject<BankAccount>): AxiosPromise<Model.PlainTrackedObject<BankAccount>> {
        return this.axios.post(`${this.apiPath}/save-bank-account`, bankAccount);
    }

    public saveBankAccountBalances(bankAccountBalances: Array<Model.PartialPlainObject<BankAccountBalance>>): AxiosPromise<Array<Model.PlainTrackedObject<BankAccountBalance>>> {
        return this.axios.post(`${this.apiPath}/save-bank-account-balances`, bankAccountBalances);
    }

    public getNoticePeriods(): AxiosPromise<Array<Model.PlainTrackedObject<NoticePeriod>>> {
        return this.axios.get(`${this.apiPath}/notice-periods`);
    }

    public saveNoticePeriods(noticePeriods: Array<Model.PartialPlainObject<NoticePeriod>>): AxiosPromise<Array<Model.PlainTrackedObject<NoticePeriod>>> {
        return this.axios.post(`${this.apiPath}/notice-periods`, noticePeriods);
    }

    // Client only properties / methods
}